import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useParams, withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';

import { getCurrentCity, getCurrentCurrency } from 'selectors';

import {
  formatParsedDate,
  getSharePreviewImageUrl,
  shouldNavigateToCurrencyYearArchive,
} from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import BanksPageContent from 'components/BanksPageContent';
import PageSidebar from 'components/PageSidebar';
import BanksPageFooter from 'components/BanksPageFooter';
import LayoutContainer from 'components/LayoutContainer';

import { Content, Wrapper } from './styles';

const SHARE_DESCRIPTION =
  'Актуальний курс валют в Україні: долар, євро, банки та обмінники — дивись всі курси онлайн';
const SHARE_TITLE = 'Курс валют в Україні на Minfin.com.ua';

const BanksPage = memo(({ location, staticContext }) => {
  const { date, currency, city } = useParams();
  const { locale, formatMessage } = useIntl();
  const sharePreviewImageUrl = getSharePreviewImageUrl({ date });

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  const currentCity = useSelector(state => getCurrentCity(state, { city }));

  const defaultMetaTitle = currentCurrency
    ? formatMessage(
        { id: `banksPage.metaTitle.withCurrency` },
        {
          currency: currentCurrency?.nameG,
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
          isCity: city ? 1 : 0,
          city: currentCity?.nameG || currentCity?.label,
        }
      )
    : formatMessage(
        {
          id: `banksPage.metaTitle`,
        },
        {
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
          isCity: city ? 1 : 0,
          city: currentCity?.nameG || currentCity?.label,
        }
      );

  //   id: `homePage.metaTitle`,
  // });

  const defaultMetaDescription = date
    ? formatMessage(
        { id: `banksPage.metaDescription.withDate` },
        {
          isCurrency: currency ? 1 : 0,
          currency: currentCurrency?.nameG,
          isCity: city ? 1 : 0,
          city: currentCity?.nameG || currentCity?.label,
        }
      )
    : formatMessage(
        {
          id: `banksPage.metaDescription`,
        },
        {
          isCurrency: currency ? 1 : 0,
          currency: currentCurrency?.nameG || currentCurrency?.label,
          isCity: city ? 1 : 0,
          city: currentCity?.nameG || currentCity?.label,
        }
      );

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  if (shouldNavigateToCurrencyYearArchive(date)) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            meta={[
              {
                property: 'og:title',
                content: SHARE_TITLE,
              },
              {
                property: 'og:description',
                content: SHARE_DESCRIPTION,
              },
              {
                property: 'og:image',
                content: sharePreviewImageUrl,
              },
              {
                name: 'twitter:title',
                content: SHARE_TITLE,
              },
              {
                name: 'twitter:description',
                content: SHARE_DESCRIPTION,
              },
              {
                name: 'twitter:image',
                content: sharePreviewImageUrl,
              },
            ]}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <BanksPageContent />
            <PageSidebar />
            <BanksPageFooter />
          </PageInner>
        </LayoutContainer>
      </Content>
    </Page>
  );
});

BanksPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

BanksPage.defaultProps = {
  staticContext: null,
};

export default withRouter(BanksPage);
